/**
 * Hook V2 — useApprenantActions
 *
 * Actions sur la fiche apprenant : Valider l'identité / Archiver / Supprimer.
 *
 *  - validate() : crée le dossier Drive + marque la fiche comme validée
 *  - archive()  : statut → Abandonné (proxy archivage)
 *  - remove()   : suppression dure Zoho
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';

export interface GeneratedDoc {
  templateName: string;
  fileName: string;
  fileId: string | null;
  fileUrl: string | null;
  filled: boolean;
  subfolder: string;
  error: string | null;
}

export interface ValidateReceipt {
  drive: {
    ok: boolean;
    folderUrl: string | null;
    error: string | null;
    fiscalYear: { label: string; range: string } | null;
  };
  docs: { ok: boolean; generated: GeneratedDoc[]; error: string | null };
  pdfEdof: {
    ok: boolean;
    fileName: string | null;
    fileUrl: string | null;
    error: string | null;
    skipped: boolean;
  };
  moodle: {
    ok: boolean;
    status: 'created' | 'found' | 'not_found' | 'skipped' | 'error';
    userId: number | null;
    newlyEnrolledCourses: number[];
    created: boolean;
    emailSent: boolean;
    error: string | null;
  };
  qualiopi: { ok: boolean; formsIssued: string[]; errors: string[] };
  complianceFlag: { ok: boolean; error: string | null };
}

export interface ValidateResult {
  id: string;
  isTest: boolean;
  receipt: ValidateReceipt;
  driveFolderUrl: string | null;
  driveError: string | null;
  modulesConnectes: string[];
  message: string;
}

export function useApprenantActions(id: string): {
  validate: () => Promise<ValidateResult>;
  archive: () => Promise<{ id: string; message: string }>;
  remove: () => Promise<{ id: string; message: string }>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['v2', 'apprenant', id] });
    queryClient.invalidateQueries({ queryKey: ['v2', 'apprenants'] });
  };

  const validateMut = useMutation({
    mutationFn: () => api.post<ValidateResult>(`/api/v1/v2/apprenants/${id}/validate`, {}),
    onSuccess: invalidate,
  });
  const archiveMut = useMutation({
    mutationFn: () =>
      api.post<{ id: string; message: string }>(`/api/v1/v2/apprenants/${id}/archive`, {}),
    onSuccess: invalidate,
  });
  const removeMut = useMutation({
    mutationFn: () => api.delete<{ id: string; message: string }>(`/api/v1/v2/apprenants/${id}`),
    onSuccess: invalidate,
  });

  return {
    validate: validateMut.mutateAsync,
    archive: archiveMut.mutateAsync,
    remove: removeMut.mutateAsync,
    isPending: validateMut.isPending || archiveMut.isPending || removeMut.isPending,
  };
}
